import React from 'react';
import ReactTooltip from 'react-tooltip';
import { Translation } from 'react-i18next';

import { IGlassesPackage } from '../../types';
import { getQSVar } from '../../utils';

import { MainWrapper } from './MainWrapper';
import { ListWrapper } from './ListWrapper';
import { ListItemWrapper } from './ListItemWrapper';
import { ListItem } from './ListItem';
import { InfoIcon } from './InfoIcon';
import { NavArrow } from './NavArrow';
import { Spacer } from './Spacer';

interface IProps {
    packages: IGlassesPackage[];
    onPackageClick(name: IGlassesPackage): void;
    selectedPackage: IGlassesPackage;
}

export class GlassesPackageSelector extends React.Component<IProps> {
    listRef: React.RefObject<HTMLUListElement>;
    scrollLeftTarget: number;
    currentScrollLeft: number;

    constructor(props: IProps) {
        super(props);

        this.listRef = React.createRef();
        this.scrollLeftTarget = 0;
        this.currentScrollLeft = 0;
    }

    handlePackageClick = (
        el: IGlassesPackage,
        isDisabled: boolean,
        e: React.MouseEvent<HTMLLIElement, MouseEvent>
    ) => {
        if (isDisabled) return;

        this.props.onPackageClick(el);
        this.adjustListPosition(e);
    };

    handleTouchEnd = () => {
        if (!this.listRef.current) return;

        this.scrollLeftTarget = this.listRef.current.scrollLeft;
    };

    handleArrowRightClick = () => {
        if (!this.listRef || !this.listRef.current) return;

        const listEl = this.listRef.current;
        const listScrollMargin = listEl.scrollWidth - listEl.clientWidth;

        this.scrollLeftTarget = listScrollMargin;
        this.getAnimationStep();
    };

    handleArrowLefClick = () => {
        this.scrollLeftTarget = 0;
        this.getAnimationStep();
    };

    adjustListPosition = (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
        if (!this.listRef || !this.listRef.current) return;

        const listEl = this.listRef.current;

        const listRightPos = this.scrollLeftTarget + listEl.clientWidth;
        const listScrollMargin = listEl.scrollWidth - listEl.clientWidth;

        const elLeftPos =
            e.currentTarget.getBoundingClientRect().left +
            this.scrollLeftTarget;
        const elRightPos =
            e.currentTarget.getBoundingClientRect().width + elLeftPos;

        const ARROW_BUTTON_SIZE = 30;

        const moveLeft = elLeftPos < this.scrollLeftTarget + ARROW_BUTTON_SIZE;
        const moveRight = elRightPos > listRightPos - ARROW_BUTTON_SIZE;

        if (!moveLeft && !moveRight) return;

        this.scrollLeftTarget = moveLeft ? 0 : listScrollMargin;
        this.getAnimationStep();
    };

    getAnimationStep = () => {
        const listEl = this.listRef.current;

        if (
            !listEl ||
            this.scrollLeftTarget === Math.floor(listEl.scrollLeft)
        ) {
            return;
        }

        window.requestAnimationFrame(this.getAnimationStep);

        this.currentScrollLeft +=
            (this.scrollLeftTarget - listEl.scrollLeft) / 10;

        listEl.scrollLeft = this.currentScrollLeft;
    };

    render() {
        const { selectedPackage, packages } = this.props;

        const backgroundColor = selectedPackage
            ? selectedPackage.backgroundColorGeneral
            : 'none';
        const activeTextColor = selectedPackage
            ? selectedPackage.textColorGeneral
            : '#000000';

        return (
            <MainWrapper>
                <NavArrow
                    backgroundColor={backgroundColor}
                    direction="left"
                    onClick={this.handleArrowLefClick}
                />
                <ListWrapper
                    ref={this.listRef}
                    onTouchEnd={this.handleTouchEnd}
                >
                    {packages.map((glassesPackage) => {
                        const isActive =
                            glassesPackage.id ===
                                (selectedPackage && selectedPackage.id) ||
                            false;

                        const isDisabled =
                            !!getQSVar('Randloos') &&
                            !glassesPackage.borderless;

                        return (
                            <ListItemWrapper
                                key={glassesPackage.id}
                                onClick={(e) => {
                                    this.handlePackageClick(
                                        glassesPackage,
                                        isDisabled,
                                        e
                                    );
                                }}
                            >
                                <ListItem
                                    active={isActive}
                                    activeTextColor={activeTextColor}
                                    isDisabled={isDisabled}
                                >
                                    {glassesPackage.name}
                                    {isDisabled && (
                                        <Translation>
                                            {(translate) => (
                                                <InfoIcon
                                                    tooltipText={translate(
                                                        'tooltipText'
                                                    )}
                                                />
                                            )}
                                        </Translation>
                                    )}
                                </ListItem>
                                <ReactTooltip />
                            </ListItemWrapper>
                        );
                    })}
                    <Spacer />
                </ListWrapper>
                <NavArrow
                    backgroundColor={backgroundColor}
                    direction="right"
                    onClick={this.handleArrowRightClick}
                />
            </MainWrapper>
        );
    }
}
